import { limparSessao } from '@/lib/auth/sessao.chave';
import { router, type Routes } from '@/router';
import createFetchClient from 'openapi-fetch';
import createClient from 'openapi-react-query';
import type { HttpMethod, PathsWithMethod } from 'openapi-typescript-helpers';
import { env } from '../env';
import { defaultLanguage, i18nInstance } from '../i18n';
import { queryClient } from '../queryClient.ts';
import { createUrlBuilder } from './url-builder.ts';
import type * as v1 from './v1.gen.d.ts';

export * from './types.ts';
export type * from './v1.gen.d';

type Schemas = keyof v1.components['schemas'];
type SimplifiedSchema<T extends Schemas> = T extends `DTOs.${infer Rest}`
  ? Rest
  : T;

export type ApiSchema<T extends SimplifiedSchema<Schemas>> =
  `DTOs.${T}` extends keyof v1.components['schemas']
    ? v1.components['schemas'][`DTOs.${T}`]
    : T extends keyof v1.components['schemas']
      ? v1.components['schemas'][T]
      : never;

type RequestWithPrecomputedClone = Request & { clonedRequest?: Request };

function getExtendedRequest(request: Request): RequestWithPrecomputedClone {
  return request as RequestWithPrecomputedClone;
}

function setRequestPrecomputedClone(request: Request) {
  getExtendedRequest(request).clonedRequest = request.clone();
}

function getRequestPrecomputedClone(request: Request) {
  const extendedRequest = getExtendedRequest(request);

  return extendedRequest.clonedRequest ?? extendedRequest;
}

export const fetchClient = createFetchClient<v1.paths>({
  baseUrl: env.VITE_API_URL,
  credentials: 'include',
});

export const urlBuilder = createUrlBuilder<v1.paths>({
  baseUrl: env.VITE_API_URL,
});

let refreshResultPromise: Promise<boolean | null> | null = null;

fetchClient.use({
  onRequest({ request }) {
    request.headers.set(
      'X-Language',
      i18nInstance.languages[0] ?? defaultLanguage,
    );

    // Preciso salvar a request original antes pois caso precise fazer um
    // refresh, o `.clone` joga uma exception com o body já consumido.
    // O clone é feito sempre porque o Firefox não expõe `request.body`.
    setRequestPrecomputedClone(request);

    return request;
  },
  async onResponse({ response, request, schemaPath }) {
    if (schemaPath.startsWith('/auth/') || response.status !== 401) {
      return;
    }

    // Se mais de uma requisição for feita ao mesmo tempo e derem 401,
    // todas vão tentar renovar o token ao mesmo tempo,
    // então usamos uma promise para simular um lock
    refreshResultPromise ??= refreshToken()
      .then(({ response: r }) => r.status === 200)
      .catch(() => false)
      .finally(() => {
        refreshResultPromise = null;
      });

    const refreshSucceeded = await refreshResultPromise;

    if (refreshSucceeded) {
      // Se conseguiu renovar o token, refaz a requisição original
      const retryResponse = await fetch(getRequestPrecomputedClone(request));

      if (retryResponse.status !== 401) {
        return retryResponse;
      }
    }

    // A pergunta "quem está logado?" se responde sozinha: o `queryFn` devolve `null` e o
    // cache passa a valer isso. Limpar a sessão aqui cancelaria a requisição em voo, e o
    // `buscarSessao` rejeitaria em vez de responder "não há sessão".
    if (schemaPath === '/auth') {
      return;
    }

    limparSessao();

    // Forço o tipo pois o router não infere o pathname https://github.com/TanStack/router/discussions/1075#discussioncomment-14572100
    const currentPathname = router.state.location.pathname as Routes;

    // Se mais de uma requisição for feita ao mesmo tempo e derem 401,
    // várias tentariam navegar para o login ao mesmo tempo.
    if (currentPathname === '/login') {
      return;
    }

    // A sessão caiu no meio do uso, então guardamos onde ele estava para voltar depois.
    const newlocation = router.buildLocation({
      to: '/login',
      search: { redirect: router.state.location.href },
    });

    await router.commitLocation({
      ...newlocation,
      replace: true,
    });
  },
});

const baseApi = createClient(fetchClient);

export const api = {
  ...baseApi,
  getQueryKey: <M extends HttpMethod, P extends PathsWithMethod<v1.paths, M>>(
    method: M,
    path: P,
  ) => {
    const queryKey = baseApi.queryOptions(method, path, null as never).queryKey;

    // O queryKey inteiro contém a requisição exata, com parâmetros e tudo,
    // mas para invalidar a query queremos usar só a parte do endpoint e método.
    return queryKey.slice(0, 2);
  },
  invalidateQueries: <M extends HttpMethod>(
    method: M,
    path: PathsWithMethod<v1.paths, M>,
  ) => {
    const queryKey = api.getQueryKey(method, path);
    return queryClient.invalidateQueries({ queryKey });
  },
};

export function postFormData<P extends PathsWithMethod<v1.paths, 'post'>>(
  path: P,
  body: Record<string, string | number | File | Blob | null | undefined>,
) {
  // @ts-expect-error O openapi-ts gera o tipo do body como objeto literal para endpoints
  // multipart/form-data, mas precisamos enviar FormData nativo. Limitação conhecida:
  // https://github.com/openapi-ts/openapi-typescript/issues/1214
  return fetchClient.POST(path, {
    body,
    bodySerializer(body: Record<string, string | number | Blob>) {
      const formData = new FormData();
      for (const [key, value] of Object.entries(body)) {
        if (value instanceof Blob) {
          formData.append(key, value, value instanceof File ? value.name : key);
        } else {
          formData.append(key, String(value));
        }
      }

      return formData;
    },
  });
}

export function refreshToken() {
  return fetchClient.GET('/auth/refresh');
}
